/**
 * MapLibre GL JS integration for EveryFerry.
 * Handles the public-facing map with route and port display.
 */

import { lineFocus, lit, pageUnder, portFocus, type Focus } from './map-pick';
import { isRow, markPanel } from './panel-marks';
import { pinsOf } from '../map/pins';
import { pageCoords, shortWay } from '../map/frame';
import { readPageGeoJson } from './page-geojson';
import { pageLanes } from '../map/seasonal-lanes';
import {
  mapStyleUrl,
  paperColor,
  routeCasingOpacity,
  routeColor,
  faintColor,
  FAINT_LINE_OPACITY,
  HIGHLIGHT_COLOR,
  ROUTE_WIDTH,
  ROUTE_WIDTH_HOVER,
  portRadius,
  HIGHLIGHT_OUTLINE,
  getPortCirclePaint,
  getPortCircleLayout,
  routeOpacity,
  DASHES,
  REST_WIDTH,
  type MapTheme,
} from '../map/style';
import { applyMapCustomizations, setBasemapLanguage, keepMapFlat } from './basemap';
// The reading column's behaviour: map mode, section nav, theme toggle
import { followPage } from './panel';

/** The page locale and its path prefix ('' in the default locale, '/fr' otherwise), rendered by the server on #map. */
function pageLocale(): string {
  return document.getElementById('map')?.dataset.locale ?? '';
}

const EMPTY_COLLECTION: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/** A seasonal line is not drawn by the routes layer but by its lanes (`pageLanes`), each stretch once, so dashes stay in step. */
const seasonalHidden = (opacity: number) => ['case', ['coalesce', ['get', 'seasonal'], false], 0, opacity];
/** A filter no feature passes: no route lit. */
const NO_ROUTE = ['in', ['get', 'id'], ['literal', []]];

function pathPrefix(): string {
  return document.getElementById('map')?.dataset.pathPrefix ?? '';
}

/** The slug a page path names, whether or not it carries the locale prefix ('' for the home page). */
function slugOf(path: string): string {
  const prefix = pathPrefix();
  const rest = prefix && (path === prefix || path.startsWith(`${prefix}/`)) ? path.slice(prefix.length) : path;
  return rest.replace(/^\//, '');
}

/** The page path of a slug in the page locale; the home page for ''. */
function pagePath(slug: string): string {
  return slug ? `${pathPrefix()}/${slug}` : pathPrefix() || '/';
}

/**
 * Replace the page-specific head tags (title, description, canonical, hreflang, Open Graph,
 * JSON-LD) with the ones the server rendered for the page now shown, then point the language
 * switcher and the address bar at what the head says. `source` is a panel's page-head template
 * content or a fetched document's head.
 */
function applyPageHead(source: Pick<ParentNode, 'querySelectorAll'>): void {
  const fresh = source.querySelectorAll('[data-page-meta]');
  if (fresh.length === 0) return;
  for (const stale of document.head.querySelectorAll('[data-page-meta]')) stale.remove();
  for (const tag of fresh) document.head.appendChild(tag.cloneNode(true));

  // The language switcher follows the page: its path in every locale (`PageHead`)
  const paths = JSON.parse(document.head.querySelector<HTMLMetaElement>('meta[name="locale-paths"]')?.content || '{}') as Record<
    string,
    string
  >;
  for (const [locale, path] of Object.entries(paths)) document.querySelector(`header a[hreflang="${locale}"]`)?.setAttribute('href', path);

  // The canonical path is the page's URL (a port's localized slug when the map navigated by English slug)
  const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (canonical) {
    const path = new URL(canonical.href).pathname;
    if (path !== window.location.pathname) pushPath(path, true);
  }
}

/** Where the address bar was before the last navigation, restored when the panel request fails. */
let previousPath: string | null = null;

/** Where the map looks: a point and a zoom. */
type View = { center: { lng: number; lat: number }; zoom: number };

/** The middle of the part of the map in view (`coveredHeight`), and its zoom. */
function viewInSight(): View | null {
  if (!map) return null;
  const box = map.getContainer();
  return { center: map.unproject([box.clientWidth / 2, (box.clientHeight - coveredHeight()) / 2]), zoom: map.getZoom() };
}

/**
 * The home state, entered locally so it is instant: the panel is cleared and the map reset at
 * once. The close partial carries the home page head and the home GeoJSON of the current locale,
 * applied when they arrive; the GeoJSON is kept for the next home entry.
 */
function enterHome(): void {
  latestPanel = ''; // a panel still on its way lands on nothing
  const locale = pageLocale(); // the answer describes this locale; a switch meanwhile makes it stale
  // What was in view before the page closes: the home state zooms out around it
  const view = viewInSight();
  document.getElementById('panel-container')?.replaceChildren();
  document.dispatchEvent(new CustomEvent('ferry:home', { detail: view }));
  fetch(getPartialUrl('close'))
    .then((response) => (response.ok ? response.text() : Promise.reject(new Error(String(response.status)))))
    .then((html) => {
      if (pageLocale() !== locale) return; // switched language meanwhile: that page brought its own home data
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const data = readPageGeoJson(doc);
      if (data) homeGeoJsonData = data;
      if (slugOf(window.location.pathname)) return; // navigated elsewhere meanwhile
      const template = doc.querySelector<HTMLTemplateElement>('template#page-head');
      if (template) applyPageHead(template.content);
      // The home path: enter the home state now if the ferry:home listener could not, the style
      // not having loaded then, and otherwise just show the data it was waiting for
      if (!data) return;
      if (isHomePage) setMapData(data);
      else enterHomeState();
    })
    .catch(() => {
      // Offline or failing: the panel is closed and the map back on the home view (enterHomeState), with the last page's data
    });
}

/** The page head a panel response carries, applied once and removed from the panel. */
function applyPanelHead(): void {
  const template = document.querySelector<HTMLTemplateElement>('#panel-container template#page-head');
  if (!template) return;
  applyPageHead(template.content);
  template.remove();
}

/** History entries record their locale, so back/forward across a language switch can re-render in place. */
function pushPath(path: string, replace = false, locale = pageLocale()): void {
  const state = { locale };
  if (replace) history.replaceState(state, '', path);
  else history.pushState(state, '', path);
}

/**
 * Switch the page to another locale in place, like panel navigation: fetch the page, swap
 * the navbar and panel inside a view transition, update the document's locale attributes
 * and re-apply the page GeoJSON (localized names) and the basemap labels without moving the
 * map. Falls back to a full load when the page cannot be fetched.
 */
async function switchLocale(path: string, push = true): Promise<void> {
  latestPanel = ''; // a panel still on its way is in the old locale
  let doc: Document;
  try {
    const response = await fetch(path);
    if (!response.ok) throw new Error(String(response.status));
    doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  } catch {
    window.location.href = path;
    return;
  }
  const newHeader = doc.querySelector('header.navbar');
  const newPanel = doc.getElementById('panel-container');
  const newMap = doc.getElementById('map');
  const header = document.querySelector('header.navbar');
  const panelContainer = document.getElementById('panel-container');
  const mapContainer = document.getElementById('map');
  if (!newHeader || !newPanel || !newMap || !header || !panelContainer || !mapContainer) {
    window.location.href = path;
    return;
  }

  if (push) pushPath(path, false, newMap.dataset.locale);
  const swap = () => {
    document.documentElement.lang = doc.documentElement.lang;
    for (const key of ['locale', 'pathPrefix'] as const) mapContainer.dataset[key] = newMap.dataset[key] ?? '';
    header.replaceWith(newHeader);
    panelContainer.replaceChildren(...newPanel.childNodes);
    followPage();
    // The map's credits, moved into the legend on a phone (initMap), go into the new one
    const credits = mapContainer.querySelector('[data-map-attribution]')?.firstElementChild;
    if (credits) doc.querySelector('#map-legend [data-map-attribution]')?.replaceChildren(credits);
    // The pins' × in the page language, for the pins the new page's data draws below
    const pinClose = doc.getElementById('port-pin-close');
    if (pinClose) document.getElementById('port-pin-close')?.replaceWith(pinClose);
    // The legend and the map toggle are server-rendered inside #map in the page language; Alpine initializes the new legend itself
    for (const selector of ['#map-legend', '.map-toggle']) {
      const fresh = doc.querySelector(selector);
      const current = mapContainer.querySelector(selector);
      if (current && fresh) current.replaceWith(fresh);
      else if (fresh) mapContainer.appendChild(fresh);
      else current?.remove();
    }
    if (typeof htmx !== 'undefined') {
      htmx.process(newHeader);
      htmx.process(panelContainer);
    }
  };
  if (document.startViewTransition) {
    const transition = document.startViewTransition(swap);
    transition.ready.catch(() => {}); // skipped (another transition started meanwhile): the swap still ran
    await transition.updateCallbackDone;
  } else swap();

  applyPageHead(doc.head);
  homeGeoJsonData = null; // named in the locale left behind; the new page or the next close partial brings its own
  applyPageGeoJson();
  if (map) setBasemapLanguage(map, pageLocale());
}

let map: maplibregl.Map | null = null;
let portTooltip: maplibregl.Popup | null = null;
/**
 * What the page points at under the mouse, wherever the mouse is (a port or a line on the map, a dot, a name or a
 * stretch of the stop list, a row of the panel): one for the whole page, so every view shows the same (`render`).
 */
let focused: Focus | null = null;
let isHomePage = false;
/** The map draws its lines only for the port focused (`hoverLines`): the home map's, a region's. */
let linesOnHover = false;

/** The home view's margin: no panel open to leave room for, so a uniform one. */
const HOME_PADDING = 24;

/**
 * Where the home map opens: Europe, the busiest region, the world being mostly empty sea at this scale. From Iceland's
 * west fjords and the Canaries to the North Cape and Turkey's south coast, the Maghreb included. A phone is too narrow
 * for that: it fits Portugal to the Aegean, Crete to southern Norway, and its height shows the rest above and below.
 */
function homeBounds(): [number, number][] {
  return isMobile()
    ? [
        [-10, 34],
        [30, 61],
      ]
    : [
        [-24.5, 27.4],
        [36.5, 71.2],
      ];
}
let homeGeoJsonData: PageGeoJson | null = null;

/** Detect if the viewport is mobile-sized (matches the CSS media query breakpoint) */
function isMobile(): boolean {
  return window.innerWidth <= 768;
}

/**
 * How much of the map the reading column covers from the bottom on a phone, as the page is now: all but the strip
 * above an open page, the folded bar in map mode, nothing on the home page. The map stays whole under the page
 * (styles.css), so framing a page is one move while the page slides, not a resize and then a move.
 */
function coveredHeight(): number {
  if (!isMobile() || !document.querySelector('.panel')) return 0;
  const root = getComputedStyle(document.documentElement);
  if (document.documentElement.classList.contains('map-mode')) return parseFloat(root.getPropertyValue('--fold-h'));
  return (map?.getContainer().clientHeight ?? 0) - parseFloat(root.getPropertyValue('--strip-h'));
}

/**
 * fitBounds padding for the part of the map in view: on a wide screen the container already excludes the reading
 * column, so it is only breathing room, a tenth of the width at most; on a phone, that and the page's cover.
 */
function getMapPadding(): { top: number; bottom: number; left: number; right: number } {
  if (!isMobile()) {
    const side = Math.min(60, Math.round((map?.getContainer().clientWidth ?? 600) / 10));
    return { top: 70, bottom: 60, left: side, right: side };
  }
  const covered = coveredHeight();
  // In map mode the legend and the toggle sit on the map's bottom edge, above the folded page: the page's ports clear them
  if (document.documentElement.classList.contains('map-mode')) return { top: 40, bottom: covered + 70, left: 30, right: 30 };
  return { top: 12, bottom: covered + 12, left: 16, right: 16 };
}

/** The coordinates the map last fitted, a page's: framed again when the page folds or unfolds over the map. */
let lastFitCoords: [number, number][] = [];
// The page folded to its bar or opened again (frontend/panel.ts): the map frames the page in what is left, as it slides
document.addEventListener('ferry:map-mode', () => {
  if (lastFitCoords.length > 0) fitBoundsToCoords(lastFitCoords, { duration: 350 });
});

/** The page theme (`data-theme`, set before paint by theme-boot.ts). */
function pageTheme(): MapTheme {
  return document.documentElement.dataset.theme === 'dark' ? 'dark' : 'light';
}

/** The GeoJSON the map shows now, re-added when the basemap changes with the theme. */
let currentData: PageGeoJson | null = null;

// A theme change swaps the basemap; the style swap drops every source and layer, so the page's data goes back on
document.addEventListener('theme:change', () => {
  if (!map || !currentData) return;
  const data = currentData;
  map.setStyle(mapStyleUrl(pageTheme()));
  map.once('style.load', () => {
    if (!map) return;
    applyMapCustomizations(map, pageTheme());
    setBasemapLanguage(map, pageLocale());
    loadMapData(data);
    render();
  });
});

// Initialize map when DOM is ready
document.addEventListener('DOMContentLoaded', initMap);

async function initMap(): Promise<void> {
  const mapContainer = document.getElementById('map') as HTMLElement | null;
  if (!mapContainer) return;

  // Read now: the panel, and the GeoJSON it embeds, may be closed before the map style has loaded
  const initialGeoJson = readPageGeoJson(document);

  // Create map instance
  map = new maplibregl.Map({
    container: 'map',
    style: mapStyleUrl(pageTheme()),
    center: [6, 50], // Europe, until the page fits its own view
    zoom: 3,
    minZoom: 1,
    maxZoom: 18,
    // Compact and bottom-left, away from the legend and the mobile map toggle
    attributionControl: false,
  });
  keepMapFlat(map);
  // The basemap's credits: in the corner, but on a phone in the legend, whose corner they would crowd
  const legendSlot = isMobile() ? document.querySelector<HTMLElement>('[data-map-attribution]') : null;
  const attribution = new maplibregl.AttributionControl({ compact: !legendSlot });
  if (legendSlot) legendSlot.replaceChildren(attribution.onAdd(map));
  else map.addControl(attribution, 'bottom-left');

  // The hover tooltip, reused from port to port
  portTooltip = portTooltipPopup();

  // Wait for map to load
  map.on('load', async () => {
    if (!map) return;

    applyMapCustomizations(map, pageTheme());
    setBasemapLanguage(map, pageLocale());

    // The page may have moved on while the style loaded: the home data if the panel was closed,
    // else whatever the document embeds now, else the payload the page was created with
    try {
      loadMapData(homeGeoJsonData ?? readPageGeoJson(document) ?? initialGeoJson);
    } catch (error) {
      // An unusable payload leaves the map empty; the handlers below still make the page work
      console.error('Map data could not be loaded:', error);
    }

    // Handle page-specific behavior
    handlePageContext(mapContainer);

    // Set up event listeners
    setupMapInteractions();
  });
}

function loadMapData(data: PageGeoJson | null): void {
  if (!map) return;
  if (!data) {
    console.error('Page GeoJSON missing: the map stays empty');
    return;
  }
  currentData = data;
  const portsData = data.ports;
  const routesData = data.routes;
  isHomePage = !!data.isHome;
  if (data.isHome) homeGeoJsonData = data;

  // Add routes layer (below ports), on a casing in the paper colour that lifts it off the basemap
  map.addSource('routes', {
    type: 'geojson',
    data: routesData,
  });

  const theme = pageTheme();
  map.addLayer({
    id: 'routes-casing',
    type: 'line',
    source: 'routes',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': paperColor(theme),
      'line-width': ROUTE_WIDTH + 3,
      'line-opacity': routeCasingOpacity(theme),
    },
  });

  map.addLayer({
    id: 'routes',
    type: 'line',
    source: 'routes',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': routeColor(theme, data.ceased),
      'line-width': ROUTE_WIDTH,
      'line-opacity': seasonalHidden(routeOpacity(theme)),
    },
  });

  // The seasonal lines, dashed, each stretch drawn once (`pageLanes`): two lines on one lane would read solid. Under the
  // all-year lines, which draw a stretch they sail too solid.
  map.addSource('seasonal', { type: 'geojson', data: pageLanes(data) });
  map.addLayer(
    {
      id: 'seasonal',
      type: 'line',
      source: 'seasonal',
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: {
        'line-color': routeColor(theme, data.ceased),
        'line-width': ROUTE_WIDTH,
        'line-opacity': routeOpacity(theme),
        'line-dasharray': DASHES,
      },
    },
    'routes',
  );

  // The rest of a longer route beyond a route page's legs: its own source, so the routes layer's filters never
  // see it; drawn thin and faint, like the faint lane in the panel, hoverable and clickable (`pageUnder`)
  map.addSource('rest', {
    type: 'geojson',
    data: data.rest ?? EMPTY_COLLECTION,
  });

  map.addLayer({
    id: 'rest',
    type: 'line',
    source: 'rest',
    layout: {
      'line-join': 'round',
    },
    paint: {
      'line-color': faintColor(theme, data.ceased),
      'line-width': REST_WIDTH,
      'line-opacity': FAINT_LINE_OPACITY,
    },
  });

  // The rest of a hovered route, orange like it but as thin and lighter as the rest is drawn
  map.addLayer({
    id: 'rest-highlight',
    type: 'line',
    source: 'rest',
    layout: { 'line-join': 'round' },
    paint: { 'line-color': HIGHLIGHT_COLOR, 'line-width': REST_WIDTH, 'line-opacity': 0.6 },
    filter: NO_ROUTE,
  });

  // A wide invisible target, so the rest can be hovered to show which route it belongs to
  map.addLayer({
    id: 'rest-hitarea',
    type: 'line',
    source: 'rest',
    layout: { 'line-join': 'round' },
    paint: { 'line-color': 'transparent', 'line-width': 16, 'line-opacity': 0 },
  });

  // Add highlighted route layer (for hover/selection)
  map.addLayer({
    id: 'routes-highlight',
    type: 'line',
    source: 'routes',
    layout: {
      'line-join': 'round',
      'line-cap': 'round',
    },
    paint: {
      'line-color': HIGHLIGHT_COLOR,
      'line-width': ROUTE_WIDTH_HOVER,
      'line-opacity': 1,
    },
    filter: ['==', ['get', 'slug'], ''],
  });

  // Add ports layer
  map.addSource('ports', {
    type: 'geojson',
    data: portsData,
  });

  map.addLayer({
    id: 'ports',
    type: 'circle',
    source: 'ports',
    layout: getPortCircleLayout(),
    paint: getPortCirclePaint(theme, data.ceased),
  });

  // Add route-associated port highlight layer (color only, no size change)
  // Shows ports that are part of a highlighted route
  map.addLayer({
    id: 'ports-route-highlight',
    type: 'circle',
    source: 'ports',
    paint: {
      'circle-color': HIGHLIGHT_COLOR,
      'circle-radius': portRadius(),
      'circle-stroke-color': HIGHLIGHT_OUTLINE,
      'circle-stroke-width': 1,
    },
    filter: ['==', ['get', 'slug'], ''],
  });

  // Add highlighted port layer (for hover/selection - larger size)
  map.addLayer({
    id: 'ports-highlight',
    type: 'circle',
    source: 'ports',
    paint: {
      'circle-color': HIGHLIGHT_COLOR,
      'circle-radius': portRadius(2),
      'circle-stroke-color': HIGHLIGHT_OUTLINE,
      'circle-stroke-width': 1,
    },
    filter: ['==', ['get', 'slug'], ''],
  });

  // Add invisible hit area layer for easier port clicking
  // Uses the same circle-sort-key as the visible layer so features[0] is always the top port
  map.addLayer({
    id: 'ports-hitarea',
    type: 'circle',
    source: 'ports',
    layout: getPortCircleLayout(),
    paint: {
      'circle-color': 'transparent',
      'circle-radius': 20, // Much larger than visible port for easier clicking
      'circle-stroke-width': 0,
    },
  });

  // Add invisible wider hit area for routes (easier tapping on mobile)
  map.addLayer(
    {
      id: 'routes-hitarea',
      type: 'line',
      source: 'routes',
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': 'transparent',
        'line-width': 20, // Wide invisible hit area for touch
        'line-opacity': 0,
      },
    },
    'routes-highlight', // Insert below the highlight layer but above the base routes
  );

  setLinesOnHover(!!data.hoverLines);
  showPins(data);
}

/** A port's name on the map: the hover tooltip, and the page's pinned ports (`showPins`), the same box. */
function portTooltipPopup(options: { className?: string; anchor?: 'left' | 'right' } = {}): maplibregl.Popup {
  // Not focused when it opens: a pinned one would ring its × on every page
  return new maplibregl.Popup({
    closeButton: false,
    closeOnClick: false,
    focusAfterOpen: false,
    offset: 12,
    ...options,
    // MapLibre adds each word as a class: an empty one (no `className`) would throw
    className: ['port-tooltip', options.className].filter(Boolean).join(' '),
  });
}

/** Whether the page's lines are drawn as legs to its destinations (a port page's, their `to`), which a destination row lights alone. */
const hasLegs = () => !!currentData?.routes.features.some((line) => line.properties?.to);

/** A port as the tooltip names it: where it is and its name. */
type TooltipPort = { geometry: GeoJSON.Geometry; properties: { slug: string; name: string } };

/** A port's tooltip, its name; none for no port, or one already pinned by name. */
function showTooltip(port: TooltipPort | undefined): void {
  if (!map || !portTooltip || !port || port.geometry.type !== 'Point' || pinnedSlugs.has(port.properties.slug))
    return void portTooltip?.remove();
  portTooltip
    .setLngLat(port.geometry.coordinates as [number, number])
    .setText(port.properties.name)
    .addTo(map);
}

/** The ports pinned on the map now (`pinsOf`), replaced with the page's data; the hover tooltip leaves them be. */
let pins: maplibregl.Popup[] = [];
let pinnedSlugs = new Set<string>();

/** Pin the page's port, or its route's two ends, in the port tooltip, with a × that leaves it (`pinsOf`). */
function showPins(data: PageGeoJson | null): void {
  for (const pin of pins) pin.remove();
  // A tap's tooltip has no mouse to leave it: it goes with the page it was on
  portTooltip?.remove();
  const shown = pinsOf(data);
  pinnedSlugs = new Set(shown.map((pin) => pin.slug));
  pins = [];
  if (!map) return;
  const template = document.getElementById('port-pin-close') as HTMLTemplateElement | null;
  if (!template) return;
  for (const { name, coordinates, anchor, closesTo } of shown) {
    const label = document.createElement('span');
    label.textContent = name;
    const close = template.content.firstElementChild!.cloneNode(true) as HTMLButtonElement;
    close.setAttribute('aria-label', `${close.getAttribute('aria-label')}: ${name}`);
    close.addEventListener('click', (event) => {
      // The pin sits on the map: its tap is not also one on the port or line under it
      event.stopPropagation();
      if (closesTo) navigateTo(`/${closesTo}`, 'port', '');
      else navigateTo('/');
    });
    const content = document.createDocumentFragment();
    content.replaceChildren(label, close);
    pins.push(portTooltipPopup({ className: 'port-pin', anchor }).setLngLat(coordinates).setDOMContent(content).addTo(map));
  }
}

/** Replace the routes and ports sources and highlight the main port, once the map has its sources. */
function setMapData(data: PageGeoJson): void {
  currentData = data;
  if (data.isHome) homeGeoJsonData = data; // the home state of the page locale, reused when the panel closes
  const routesSource = map?.getSource('routes') as maplibregl.GeoJSONSource | undefined;
  const portsSource = map?.getSource('ports') as maplibregl.GeoJSONSource | undefined;
  const restSource = map?.getSource('rest') as maplibregl.GeoJSONSource | undefined;
  const seasonalSource = map?.getSource('seasonal') as maplibregl.GeoJSONSource | undefined;
  if (!routesSource || !portsSource || !restSource || !seasonalSource) return;
  routesSource.setData(data.routes);
  seasonalSource.setData(pageLanes(data));
  portsSource.setData(data.ports);
  restSource.setData(data.rest ?? EMPTY_COLLECTION);
  map?.setPaintProperty('routes', 'line-color', routeColor(pageTheme(), data.ceased));
  map?.setPaintProperty('seasonal', 'line-color', routeColor(pageTheme(), data.ceased));
  map?.setPaintProperty('rest', 'line-color', faintColor(pageTheme(), data.ceased));
  // The ports drawn as the page's lines are: the rings of a ceased page grey with them
  for (const [property, value] of Object.entries(getPortCirclePaint(pageTheme(), data.ceased)))
    map?.setPaintProperty('ports', property, value);
  setLinesOnHover(!!data.hoverLines);
  focused = null;
  render();
  showPins(data);
}

/** The lines hidden but for the port focused (`hoverLines`), or all drawn. */
function setLinesOnHover(on: boolean): void {
  linesOnHover = on;
  for (const layer of ['routes', 'routes-casing', 'routes-hitarea', 'rest', 'rest-hitarea']) {
    map?.setFilter(layer, null);
    map?.setLayoutProperty(layer, 'visibility', on ? 'none' : 'visible');
  }
}

/** Apply the page GeoJSON embedded in the document to the map sources. */
function applyPageGeoJson(): void {
  const data = readPageGeoJson(document);
  if (data) setMapData(data);
}

/**
 * Apply page GeoJSON and fit map bounds to the data.
 * Called after HTMX content has settled.
 */
function applyPageGeoJsonAndFitBounds(): void {
  if (!map) return;

  isHomePage = false;
  focus(null);

  const data = readPageGeoJson(document);
  if (!data) return;
  setMapData(data);

  fitBoundsToCoords(pageCoords(data));
}

/** Fit map bounds to an array of coordinates */
function fitBoundsToCoords(coords: [number, number][], options?: maplibregl.FitBoundsOptions): void {
  lastFitCoords = coords;
  if (!map || coords.length === 0) return;
  // On a wide screen the container may have just changed shape (a panel opened beside the home map): fit to its size now, not the last frame's
  map.resize();
  const lngLatBounds = new maplibregl.LngLatBounds();
  shortWay(coords).forEach((coord) => lngLatBounds.extend(coord));
  map.fitBounds(lngLatBounds, {
    padding: getMapPadding(),
    duration: 1000,
    // A lone point (a port no line calls at) is centred at a zoom that still shows where it is
    ...(coords.length === 1 && { maxZoom: 9 }),
    ...options,
  });
}

function handlePageContext(mapContainer: HTMLElement): void {
  if (!map) return;

  // Apply page-specific GeoJSON if present (for port/route pages)
  applyPageGeoJson();

  // Check for page-specific bounds (port, route, or operator pages)
  const boundsJson = mapContainer.dataset.portBounds || mapContainer.dataset.routeBounds || mapContainer.dataset.operatorBounds;

  if (boundsJson) {
    try {
      const bounds = JSON.parse(boundsJson) as [number, number][];
      fitBoundsToCoords(bounds);
    } catch (e) {
      console.error('Error parsing bounds:', e);
    }
    return;
  }

  // Homepage: Europe, instantly, with no panel open to leave room for: a uniform margin
  if (isHomePage) fitBoundsToCoords(homeBounds(), { duration: 0, padding: HOME_PADDING });
  // Any other page (a region's): its own ports, not the grey ones its lines reach
  else if (currentData) fitBoundsToCoords(pageCoords(currentData));
}

function setupMapInteractions(): void {
  if (!map) return;

  // Port hover - use hitarea for larger target, show highlight
  map.on('mouseenter', 'ports-hitarea', () => {
    if (map) map.getCanvas().style.cursor = 'pointer';
  });

  map.on('mouseleave', 'ports-hitarea', () => {
    if (map) map.getCanvas().style.cursor = '';
    showTooltip(undefined);
    focus(null);
  });

  map.on('mousemove', 'ports-hitarea', (e: maplibregl.MapLayerEvent) => {
    if (e.features && e.features.length > 0) {
      const feature = e.features[0];
      const { slug, route } = feature.properties as { slug: string; route?: string };
      // A port takes over from the line it sits on; its panel fetched ahead, the one a click opens
      if (focus(portFocus(slug, drawnLines(), !!currentData?.page))) prefetchPartial(route ?? slug, route ? 'route' : 'port');
      showTooltip(feature as unknown as TooltipPort);
    }
  });

  // A line under the mouse, solid or faint, focuses its route alone (`lineFocus`, the one a click opens), whole: the
  // pointer, and its panel fetched ahead, only where that page is not the one shown (ports take priority)
  const LINES = ['routes-hitarea', 'rest-hitarea'];
  map.on('mousemove', LINES, (e: maplibregl.MapLayerEvent) => {
    if (!map || linesOnHover) return;
    if (map.queryRenderedFeatures(e.point, { layers: ['ports-hitarea'] }).length > 0) return;
    const next = lineFocus(e.features ?? []);
    const opens = next?.page === currentData?.page ? undefined : next?.page;
    map.getCanvas().style.cursor = opens ? 'pointer' : '';
    if (focus(next) && opens) prefetchPartial(opens, 'route');
  });

  map.on('mouseleave', LINES, () => {
    if (!map || linesOnHover) return;
    map.getCanvas().style.cursor = '';
    // Off the line but still on a port over it: the port keeps the focus
    if (!focused?.ports.length) focus(null);
  });

  // Mobile: prefetch on touchstart (100-300ms head start before click fires)
  map.getCanvas().addEventListener(
    'touchstart',
    (e: TouchEvent) => {
      if (!map || e.touches.length !== 1) return;
      const touch = e.touches[0];
      const point: maplibregl.PointLike = [
        touch.clientX - map.getCanvas().getBoundingClientRect().left,
        touch.clientY - map.getCanvas().getBoundingClientRect().top,
      ];
      const portFeatures = map.queryRenderedFeatures(point, { layers: ['ports-hitarea'] });
      if (portFeatures.length > 0) {
        const { slug, route } = portFeatures[0].properties as { slug: string; route?: string };
        prefetchPartial(route ?? slug, route ? 'route' : 'port');
        return;
      }
      const slug = linesOnHover ? undefined : pageUnder(map.queryRenderedFeatures(point, { layers: LINES }), currentData?.page);
      if (slug) prefetchPartial(slug, 'route');
    },
    { passive: true },
  );

  // Click handlers - ports take priority (routes handler checks for port overlap)
  map.on('click', 'ports-hitarea', (e: maplibregl.MapLayerEvent) => {
    if (e.features && e.features.length > 0) {
      // On a route page, a port of its lines but the trip's ends opens the route page of the same line it gives
      const { slug, route, name } = e.features[0].properties as { slug: string; route?: string; name?: string };
      // A tap lights what a mouse would have lit hovering it, while its panel comes
      focus(portFocus(slug, drawnLines(), !!currentData?.page));
      navigateTo(`/${route ?? slug}`, route ? 'route' : 'port', route ? '' : name);
    }
  });

  map.on('click', LINES, (e: maplibregl.MapLayerEvent) => {
    if (!map || linesOnHover) return;
    // Check if we clicked on a port hitarea (ports take priority)
    const portFeatures = map.queryRenderedFeatures(e.point, { layers: ['ports-hitarea'] });
    if (portFeatures.length > 0) return;
    const slug = pageUnder(e.features ?? [], currentData?.page);
    if (slug) navigateTo(`/${slug}`, 'route');
  });
}

/**
 * Point every view at `next` (`Focus`), or at nothing: the map's orange layers, and in the panel every element that
 * says what it stands for (`data-map-port`, a port's slug; `data-map-routes`, route ids; `data-map-end`, the port a
 * destination row ends at, lit and named with its routes), the stop list's dots,
 * names and lines and the operators' rows alike, marked `data-map-hover`. Whether it changed anything (a mouse moving
 * over the same thing does not; a focus is built with its fields in one order, so its JSON tells).
 */
function focus(next: Focus | null): boolean {
  if (JSON.stringify(next) === JSON.stringify(focused)) return false;
  focused = next;
  render();
  return true;
}

/** Every line of the page's data, solid and faint: all of them, not only those on screen. */
function drawnLines(): Array<{ properties: Record<string, unknown> }> {
  const features = [...(currentData?.routes.features ?? []), ...(currentData?.rest?.features ?? [])];
  return features as unknown as Array<{ properties: Record<string, unknown> }>;
}

/** Show `focused` everywhere (`lit`): the routes whole, or their legs to `focused.to`, and their ports on the map and in the panel, the ports, the page's own port. */
function render(): void {
  if (!map) return;
  if (linesOnHover) return hoverLines(focused);
  const { ports } = lit(focused, drawnLines());
  const routes = focused?.routes ?? [];
  const lines = ['in', ['get', 'id'], ['literal', routes]];
  // A route page's own route, or a port page's leg to a destination, alone of its route
  const filter = focused?.page
    ? ['all', lines, ['==', ['get', 'slug'], focused.page]]
    : focused?.to
      ? ['all', lines, ['==', ['get', 'to'], focused.to]]
      : lines;
  map.setFilter('routes-highlight', filter);
  map.setFilter('rest-highlight', filter);
  map.setFilter('ports-route-highlight', ['in', ['get', 'slug'], ['literal', ports]]);
  map.setFilter('ports-highlight', [
    'any',
    ['in', ['get', 'slug'], ['literal', focused?.ports ?? []]],
    ['==', ['get', 'id'], currentData?.mainPortId ?? -1],
  ]);
  markRows(new Set(ports), routes);
}

/** The row of a list in the panel under the mouse, marked alone of the rows (`markPanel`). */
let panelRow: HTMLElement | null = null;

/** Mark in the panel the elements standing for these ports or routes (`markPanel`). */
function markRows(ports: Set<string>, routes: number[]): void {
  const panel = document.getElementById('panel-container');
  if (panel) markPanel(panel, ports, routes, panelRow);
}

/** A map that draws its lines on hover only (`linesOnHover`): those of the port or the routes focused, whole, solid and faint (dashed, a seasonal one), their ports lit; none, the map bare. */
function hoverLines(hovered: Focus | null): void {
  if (!map) return;
  const { ports } = lit(hovered, drawnLines());
  // The lines of a port hovered, or of a route row (a region's ways in)
  const on = !!hovered?.routes.length;
  const shown = on ? ['in', ['get', 'id'], ['literal', hovered.routes]] : NO_ROUTE;
  for (const layer of ['routes', 'routes-casing', 'rest']) {
    map.setFilter(layer, shown);
    map.setLayoutProperty(layer, 'visibility', on ? 'visible' : 'none');
  }
  // The seasonal lanes of the lines focused
  const seasonal = map.getSource('seasonal') as maplibregl.GeoJSONSource | undefined;
  if (currentData) seasonal?.setData(pageLanes(currentData, hovered?.routes));
  map.setFilter('routes-highlight', NO_ROUTE);
  map.setFilter('rest-highlight', NO_ROUTE);
  map.setFilter('ports-highlight', ['in', ['get', 'slug'], ['literal', hovered?.ports ?? []]]);
  map.setFilter('ports-route-highlight', ['in', ['get', 'slug'], ['literal', ports]]);
  markRows(new Set(ports), hovered?.routes ?? []);
}

type EntityType = 'port' | 'route' | 'operator' | 'region';

/** Build the partial URL for a given slug and optional entity type, in the page locale */
function getPartialUrl(slug: string, entityType?: EntityType): string {
  const path = entityType ? `/partials/${entityType}/${slug}` : `/partials/${slug}`;
  return pathPrefix() ? `${path}?locale=${pageLocale()}` : path;
}

/**
 * The panels asked for and not yet shown, by URL: a request in flight or its answer. A prefetch (a hover, a touch, which
 * comes 100-300 ms before its click) starts one at once; the click then waits on that very request rather than sending a
 * second, so even a head start shorter than the request counts. An answer leaves as it lands: the browser keeps it
 * from there (partials are cached, `PANEL_CACHE`).
 */
const panels = new Map<string, Promise<{ ok: boolean; html: string }>>();

function panelAnswer(url: string): Promise<{ ok: boolean; html: string }> {
  const loading = panels.get(url);
  if (loading) return loading;
  const answer = fetch(url)
    .then(async (response) => ({ ok: response.ok, html: await response.text() }))
    .catch(() => ({ ok: false, html: '' }))
    .finally(() => panels.delete(url));
  panels.set(url, answer);
  return answer;
}

/** Start loading a panel the user may open next, so its click finds it loaded or on its way. */
function prefetchPartial(slug: string, entityType?: EntityType): void {
  void panelAnswer(getPartialUrl(slug, entityType));
}

/** Prefetch the panel of the link `target` is in, if any. */
function prefetchLink(target: HTMLElement): void {
  const link = target.closest<HTMLElement>('a[href^="/"]');
  const slug = link && slugOf(link.getAttribute('href')!);
  if (slug) prefetchPartial(slug, (link.dataset.type as EntityType) || undefined);
}

/** The panel last asked for: an answer that comes back after a later one is dropped. */
let latestPanel = '';

/** How long a panel may take before the tap shows its skeleton: a prefetched one comes at once, with no flash. */
const SKELETON_AFTER_MS = 80;

/** The page's skeleton panel in the container (`#panel-skeleton`), its name filled in when the tap knew it. */
function showSkeleton(name: string): void {
  const template = document.getElementById('panel-skeleton') as HTMLTemplateElement | null;
  const container = document.getElementById('panel-container');
  if (!template || !container) return;
  const skeleton = template.content.cloneNode(true) as DocumentFragment;
  for (const el of skeleton.querySelectorAll('[data-page-name]')) el.textContent = name;
  const opening = !container.querySelector('.panel');
  container.replaceChildren(skeleton);
  // A panel opening takes the map's room on a wide screen (`body:has(.panel)`): the map draws at its new size at once
  if (opening) map?.resize();
}

/**
 * Show the panel at `url` in `#panel-container`, from the prefetch when there is one, through `htmx.swap` so its
 * lifecycle (htmx:afterSwap, htmx:afterSettle) runs as for any panel. Slower than a tap can wait, its skeleton shows
 * first, named `name`. A panel that fails (a page gone meanwhile, a network error) takes the page back
 * to where it was.
 */
async function showPanel(url: string, name = ''): Promise<void> {
  latestPanel = url;
  let skeleton = false;
  const timer = setTimeout(() => {
    if (url !== latestPanel) return;
    skeleton = true;
    showSkeleton(name);
  }, SKELETON_AFTER_MS);
  const { ok, html } = await panelAnswer(url);
  clearTimeout(timer);
  if (url !== latestPanel) return;
  const container = document.getElementById('panel-container');
  if (!container) return;
  if (!ok) {
    const back = previousPath ?? '/';
    previousPath = null;
    pushPath(back, true);
    focus(null);
    // The page on screen stays; one the skeleton replaced comes back
    if (!skeleton) return;
    const slug = slugOf(back);
    if (slug) void showPanel(getPartialUrl(slug));
    else enterHome();
    return;
  }
  htmx.swap(container, html, { swapStyle: 'innerHTML', transition: true }, { eventInfo: { target: container } });
}

function navigateTo(path: string, entityType?: EntityType, name = ''): void {
  const slug = slugOf(path);

  if (typeof htmx !== 'undefined' && document.getElementById('panel-container')) {
    previousPath = window.location.pathname + window.location.search + window.location.hash;
    pushPath(pagePath(slug));
    if (!slug) {
      enterHome();
      return;
    }
    // Typed partial URL when the type is known (avoids server-side slug resolution)
    void showPanel(getPartialUrl(slug, entityType), name);
    return;
  }

  // Fallback to full page navigation
  window.location.href = path;
}

// Handle HTMX events for map updates
document.body.addEventListener('htmx:afterSwap', (event: Event) => {
  const customEvent = event as CustomEvent<{ elt: Element }>;
  htmx.process(customEvent.detail.elt);

  // The page head the panel carries: title, description, canonical, hreflang, Open Graph, JSON-LD
  applyPanelHead();
});

// Apply page GeoJSON after HTMX content has settled (DOM is fully updated)
document.body.addEventListener('htmx:afterSettle', (event: Event) => {
  const detail = (event as CustomEvent).detail;
  const target = detail.target;
  if (target?.id === 'panel' || target?.id === 'panel-container') {
    applyPageGeoJsonAndFitBounds();
  }
});

// Handle browser back/forward
window.addEventListener('popstate', (event: PopStateEvent) => {
  // An entry from another locale: re-render the page in place rather than parsing its slug with the wrong prefix
  const entryLocale = (event.state as { locale?: string } | null)?.locale;
  if (entryLocale && entryLocale !== pageLocale()) {
    switchLocale(window.location.pathname, false);
    return;
  }

  const slug = slugOf(window.location.pathname);
  if (!slug) {
    enterHome();
    return;
  }

  // Re-fetch the partial — DOM is stale after back/forward
  void showPanel(getPartialUrl(slug));
  // htmx:afterSettle will apply GeoJSON and fit bounds
});

/**
 * Enter homepage state: restore the home GeoJSON, hide routes, zoom out gently. The home
 * GeoJSON is the one the home page embedded, or the one the close partial brings (enterHome).
 */
function enterHomeState(from: View | null = null): void {
  if (!map) return;

  lastFitCoords = [];
  focus(null);
  // The home data, or until it comes (a page opened by its own address) at least none of the closed page's pins
  if (homeGeoJsonData) setMapData(homeGeoJsonData);
  else showPins(null);

  // Enter homepage mode: hide routes, back to the home view
  isHomePage = true;
  setLinesOnHover(true);
  render();

  // Out to the home view's zoom, however long the page's route was, around what was in view: the region stays, seen
  // as the home page shows the world; a view already wider stays as it is
  if (from) {
    map.resize();
    const home = new maplibregl.LngLatBounds();
    homeBounds().forEach((coord) => home.extend(coord));
    const homeZoom = map.cameraForBounds(home, { padding: HOME_PADDING })?.zoom ?? from.zoom;
    map.easeTo({ center: [from.center.lng, from.center.lat], zoom: Math.min(from.zoom, homeZoom), duration: 1000 });
  }
}

// Handle home navigation (close panel), from what was in view
document.addEventListener('ferry:home', (event) => enterHomeState((event as CustomEvent<View | null>).detail));

// Prefetch panel links on hover (scoped to panel container only)
const panelEl = document.getElementById('panel-container');
if (panelEl) {
  panelEl.addEventListener('mouseleave', () => {
    panelRow = null;
    focus(null);
    showTooltip(undefined);
  });
  // What the mouse is over in the panel focuses what it stands for; a link under it gets its panel prefetched
  panelEl.addEventListener('mouseover', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const over = target.closest<HTMLElement>('[data-map-port], [data-map-routes]');
    const row = over && isRow(over) ? over : null;
    // Another row standing for the same lines changes no focus, but the mark moves to it
    const moved = row !== panelRow;
    panelRow = row;
    // A row ending at a port (a destination, `data-map-end`) lights its lines and that port, its name shown: on a line of
    // ten stops, which one the row is
    const end = over?.dataset.mapEnd;
    const changed = focus(
      over?.dataset.mapPort
        ? portFocus(over.dataset.mapPort, drawnLines(), !!currentData?.page)
        : over?.dataset.mapRoutes
          ? { ports: end ? [end] : [], routes: over.dataset.mapRoutes.split(' ').map(Number), ...(end && hasLegs() && { to: end }) }
          : null,
    );
    if (moved && !changed) render();
    const named = over?.dataset.mapPort ?? end;
    if (changed || moved)
      showTooltip(
        named ? (currentData?.ports.features.find((port) => port.properties?.slug === named) as TooltipPort | undefined) : undefined,
      );
    prefetchLink(target);
  });
  // A touch has no hover: its link starts loading as the finger lands, ahead of the click
  panelEl.addEventListener('touchstart', (e: TouchEvent) => prefetchLink(e.target as HTMLElement), { passive: true });
}

// Event delegation: route all panel/header link clicks through navigateTo()
document.addEventListener('click', (e: MouseEvent) => {
  const target = e.target as HTMLElement;

  // Language switcher links (hreflang) re-render the page in its other locale
  const localeLink = target.closest('header a[hreflang]') as HTMLAnchorElement | null;
  if (localeLink) {
    e.preventDefault();
    localeLink.closest('details')?.removeAttribute('open');
    if (!localeLink.hasAttribute('aria-current')) switchLocale(localeLink.getAttribute('href')!);
    return;
  }

  // Links with href="/" inside panel or header
  const link = target.closest('#panel-container a[href^="/"], header a[href^="/"]') as HTMLElement | null;
  if (link) {
    e.preventDefault();
    navigateTo(link.getAttribute('href')!, (link.dataset.type as EntityType) || undefined);
    return;
  }

  // Close button (button with data-href)
  const btn = target.closest('#panel-container button[data-href]');
  if (btn) {
    e.preventDefault();
    navigateTo((btn as HTMLElement).getAttribute('data-href')!);
    return;
  }
});

// Configure HTMX: enable View Transitions, disable history management
document.addEventListener('DOMContentLoaded', () => {
  if (typeof htmx !== 'undefined') {
    htmx.config.globalViewTransitions = true;
    htmx.config.historyEnabled = false;
  }
  pushPath(window.location.pathname + window.location.search + window.location.hash, true);
});
